/**
 * `/__design/screens` — the real screens, rendered with no backend. Dev only.
 *
 * ## Why this is a fixture and not a running stack
 *
 * `jmt-api` needs `JMT__API__KEK` and `JMT__API__SIGNING_KEY`, and neither is in `.env`.
 * Generating a KEK to look at a screen would be actively harmful: it is the key-encryption
 * key for stored exchange credentials, so a new one makes anything already encrypted in the
 * database undecryptable. And reaching `/markets` means signing in, which needs the
 * operator's password and a code from their authenticator.
 *
 * So the screens run against a **stubbed `fetch`** instead. `MarketsScreen` below is the
 * same module the router mounts; only the responses are fake.
 *
 * ## Why the network and not the query cache
 *
 * The first version of this seeded the React Query cache with `setQueryData` and set
 * `staleTime: Infinity`. It half-worked, and the half that failed is instructive:
 * `SystemScreen` sets `refetchInterval` on both its queries — 10s on deep health, 60s on
 * data health, deliberately, because a stale "Healthy" during an incident is the most
 * misleading thing that screen could show. **A refetch interval ignores `staleTime`.** So
 * ten seconds after the screen mounted, the poll fired, hit nothing, and replaced the
 * seeded data with an error. The preview looked correct and then quietly broke.
 *
 * Stubbing `fetch` puts the fixture *below* everything the screen does, so polling,
 * retries, the generated client, the URL construction and the deserialisation are all real
 * and all exercised. The interval still fires; it just gets an answer.
 *
 * ## What this proves and what it does not
 *
 * Real: layout, type, colour, spacing, the chart, the generated client, query keys, polling,
 * and every state that depends on the *shape* of the response — the stale age chip, the
 * synthetic-bar legend, the degraded-health hero.
 *
 * Not real: the API contract itself. If the server's schema drifts from
 * `openapi.json`, this page will not notice — `make schema-check` is what catches that.
 *
 * The fixture is deliberately awkward rather than a clean happy path: one stale series, one
 * ageing, three synthetic bars, an open gap, and a degraded health check. A preview that
 * only shows the good state cannot catch the bad one.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLayoutEffect, useMemo, useState } from 'react';

import {
  Freshness, HealthStatus, InstrumentKind, InstrumentStatus, StrategyState, Timeframe,
  type BarListResponse, type DataHealthResponse, type DeepHealthResponse,
  type FleetCandidate, type FleetFillsResponse, type FleetPositionsResponse,
  type FleetResponse,
  type InstrumentResponse,
} from '../api/generated';
import { SegmentedControl } from '../design/components/controls/SegmentedControl.jsx';
import { Banner } from '../design/components/messages/Banner.jsx';
import { FleetScreen } from './FleetScreen';
import { MarketsScreen } from './MarketsScreen';
import { ResearchScreen } from './ResearchScreen';
import { PLACEHOLDER_COPY, Placeholder } from './Placeholder';
import { CompanyScreen } from './stocks/CompanyScreen';
import { CoverageScreen } from './stocks/CoverageScreen';
import {
  COMPANIES,
  COVERAGE,
  GROWTH_RUN,
  NEWS_DESK,
  NEWS_STORIES,
  SCORECARD,
  SCREENER_RUN,
  TRUMP_BOARD,
  TRUMP_BOARD_COUNTS,
  TRUMP_RATES,
  TRUMP_STATEMENTS,
} from './stocks/fixture';
import { TrumpWatchScreen } from './stocks/TrumpWatchScreen';
import { NewsScreen } from './stocks/NewsScreen';
import { ScorecardScreen } from './stocks/ScorecardScreen';
import { ScreenerScreen } from './stocks/ScreenerScreen';
import { SystemScreen } from './SystemScreen';

/**
 * The Stocks section's four screens, rendered from `stocks/fixture.ts` rather than over the
 * stubbed fetch.
 *
 * ⚠ **They do not go through `ROUTES`, and that is a real gap rather than a shortcut.** The other
 * three screens exercise the generated client, the query keys and the polling because there is an
 * endpoint to stub; `/api/v1/stocks/…` has none, so there is nothing to put behind a regexp. What
 * these previews prove is the layout, the type, the responsive sweep and every state that depends
 * on the *shape* of the data — which is all the design mockup could be checked against anyway.
 */
const STOCKS: Readonly<Record<string, () => React.JSX.Element>> = {
  screener: () => <ScreenerScreen run={SCREENER_RUN} />,
  // 🔴 The growth layout, which had no preview at all until 2026-08-12 — see GROWTH_RUN.
  growth: () => <ScreenerScreen run={GROWTH_RUN} ruleset="growth" />,
  // 🔴 News (D82), which had NO preview until now — see NEWS_STORIES for the defect that
  // shipped because of it.
  news: () => <NewsScreen items={NEWS_STORIES} desk={NEWS_DESK} sort="recent" onSort={() => {}} />,
  scorecard: () => <ScorecardScreen card={SCORECARD} />,
  coverage: () => <CoverageScreen coverage={COVERAGE} />,
  // 🔴 Trump Watch (D77/D78). Rendered with the awkward fixture rather than a happy path —
  // see TRUMP_STATEMENTS for which state each row is there to exercise.
  trump: () => (
    <TrumpWatchScreen
      items={TRUMP_STATEMENTS}
      rates={TRUMP_RATES}
      filter={null}
      onFilter={() => {}}
      companies={TRUMP_BOARD}
      counts={TRUMP_BOARD_COUNTS}
    />
  ),
  // Not a nav id. `company` is a detail route in the app, and the preview needs a way to reach it
  // that does not depend on a URL parameter the segmented control cannot set.
  company: () => <CompanyScreen company={COMPANIES.US4824801009!} />,
};

const INSTRUMENT_ID = 31;

const INSTRUMENT: InstrumentResponse = {
  id: INSTRUMENT_ID,
  exchange: 'okx',
  symbol: 'BTC/EUR',
  base: 'BTC',
  quote: 'EUR',
  kind: InstrumentKind.SPOT,
  status: InstrumentStatus.TRADING,
  tick_size: '0.1',
  lot_size: '0.00001',
  min_quantity: '0.00001',
  min_notional: '5',
  max_leverage: null,
  max_price_limit_pct: null,
  float_price_limit_pct: null,
  fetched_at: '2026-08-01T12:00:00Z',
};

/** A deterministic walk. No `Math.random`: a fixture that differs between two runs cannot
    be compared against a screenshot from yesterday. */
function bars(count: number, startTs: number, stepS: number, seed = 71_204.5): BarListResponse {
  const out = [];
  let price = seed;
  for (let i = 0; i < count; i += 1) {
    // A cheap deterministic oscillator with two incommensurate periods, so the series has
    // shape without repeating on screen.
    const drift = Math.sin(i / 17) * 320 + Math.sin(i / 5.5) * 110 + Math.cos(i / 41) * 480;
    const open = price;
    const close = seed + drift;
    const high = Math.max(open, close) + Math.abs(Math.sin(i / 3)) * 90;
    const low = Math.min(open, close) - Math.abs(Math.cos(i / 3.4)) * 90;
    price = close;
    out.push({
      ts: new Date((startTs + i * stepS) * 1000).toISOString(),
      open: open.toFixed(1),
      high: high.toFixed(1),
      low: low.toFixed(1),
      close: close.toFixed(1),
      volume: (12 + Math.abs(Math.sin(i / 7)) * 40).toFixed(4),
      quote_volume: null,
      trades: 400 + (i % 130),
      // Three manufactured bars, so the legend's synthetic warning is exercised rather
      // than assumed.
      synthetic: i === 118 || i === 119 || i === 240,
    });
  }
  return { instrument_id: INSTRUMENT_ID, timeframe: Timeframe._1H, bars: out };
}


/**
 * 🔴 **The rest of the ingested universe, and this fixture exists because its absence shipped a
 * defect straight to production.**
 *
 * The four hand-written series above were the whole instrument fixture, so the Markets picker was
 * designed and reviewed against **three symbols**. Production ingests **74**, and the chip grid
 * that looked airy against three became fourteen rows that pushed the chart below the fold — on
 * the operator's screen, not in any preview.
 *
 * This is the lesson `BUILD-LOG` already records in another form: *a fixture with only the easy
 * case is a specification with a hole in it*. A picker's fixture has to carry the **cardinality**
 * the real screen has, not just one row of each shape, because layout defects live in the count.
 *
 * Symbols are the real ones from `config/jmt-marketdata.yaml`.
 */
const MORE_SYMBOLS = [
  'AAVE', 'ADA', 'ALGO', 'ALLO', 'APE', 'APT', 'ARB', 'AVAX', 'AXS', 'BABY', 'BCH', 'BICO',
  'BIO', 'BNB', 'CHZ', 'CORE', 'DOGE', 'DOT', 'DYDX', 'EDGE', 'EIGEN', 'ENA', 'ETC', 'ETHFI',
  'FIL', 'GRAM', 'HMSTR', 'HYPE', 'ICP', 'INJ', 'IRYS', 'JTO', 'KAT', 'LINK', 'LTC', 'MEGA',
  'METIS', 'MMT', 'MON', 'NEAR', 'NES', 'NIGHT', 'OFC', 'OKB', 'ONDO', 'OP', 'ORDI', 'PENDLE',
  'PENGU', 'PEPE', 'PUMP', 'RE', 'RENDER', 'RESOLV', 'SAND', 'SEI', 'SPACE', 'STRK', 'SUI',
  'TAO', 'TIA', 'TRUMP', 'TRX', 'UNI', 'W', 'WLD', 'XLM', 'XPL', 'XRP', 'ZEC', 'ZRO',
];

const MORE_SERIES = MORE_SYMBOLS.map((base, i) => ({
  instrument_id: 200 + i,
  exchange: 'okx',
  symbol: `${base}/EUR`,
  timeframe: Timeframe._1H,
  bars: 8_000 + i * 37,
  first_bar_ts: '2025-01-01T00:00:00Z',
  last_bar_ts: '2026-08-01T11:00:00Z',
  age_s: 900,
  missing_bars: 0,
  open_gaps: 0,
  freshness: Freshness.FRESH,
}));

const HEALTH: DataHealthResponse = {
  checked_at: '2026-08-01T12:00:00Z',
  series: [
    { instrument_id: 31, exchange: 'okx', symbol: 'BTC/EUR', timeframe: Timeframe._1H,
      bars: 23_599, first_bar_ts: '2023-11-14T00:00:00Z', last_bar_ts: '2026-08-01T11:00:00Z',
      age_s: 900, missing_bars: 4, open_gaps: 1, freshness: Freshness.FRESH },
    { instrument_id: 31, exchange: 'okx', symbol: 'BTC/EUR', timeframe: Timeframe._1M,
      bars: 41_200, first_bar_ts: '2026-06-02T00:00:00Z', last_bar_ts: '2026-08-01T11:56:00Z',
      age_s: 240, missing_bars: 0, open_gaps: 0, freshness: Freshness.STALE },
    { instrument_id: 44, exchange: 'okx', symbol: 'ETH/EUR', timeframe: Timeframe._1H,
      bars: 18_004, first_bar_ts: '2024-02-01T00:00:00Z', last_bar_ts: '2026-08-01T11:00:00Z',
      age_s: 3_200, missing_bars: 12, open_gaps: 0, freshness: Freshness.AGEING },
    { instrument_id: 52, exchange: 'okx', symbol: 'SOL/EUR', timeframe: Timeframe._1H,
      bars: 9_112, first_bar_ts: '2025-03-19T00:00:00Z', last_bar_ts: '2026-08-01T11:00:00Z',
      age_s: 900, missing_bars: 0, open_gaps: 0, freshness: Freshness.FRESH },
    ...MORE_SERIES,
  ],
};

/* `degraded` rather than `ok`, deliberately. A preview that only ever shows the healthy
   state is a preview that cannot catch the unhealthy one, and this screen's whole job is
   reporting trouble — so the fixture has the key ring up and the exchange reachable, and
   the market-data stream down. */
const DEEP_HEALTH: DeepHealthResponse = {
  service: 'jmt-api',
  version: '0.1.0',
  status: HealthStatus.DEGRADED,
  reason: 'market-data stream has not published a bar in 6 minutes',
  /* These key names are the server's, and `SystemScreen`'s `DEPENDENCY_LABELS` looks them
     up literally — `database` and `keyring`, not `postgres` and `key_ring`. Getting them
     wrong renders `—` in every row, which is the screen correctly reporting that it did not
     get an answer, and is indistinguishable from the service being down. Worth the note:
     the first draft of this fixture had the plausible names and the preview looked broken. */
  checks: {
    database: true,
    keyring: true,
    redis: true,
    exchange_reachable: true,
    marketdata_stream: false,
  },
};

/**
 * A 30-point equity curve as decimal strings, the shape `sparkline` arrives in (H6 — money is a
 * string). Deterministic for the same reason `bars` is: a fixture that differs between two runs
 * cannot be compared against yesterday's screenshot.
 *
 * `drift` is the total move over the window in percent and `wobble` how far it wanders getting
 * there.
 *
 * ⚠ **The wobble is enveloped to zero at both ends, and the first version of this was not.**
 * `Sparkline` takes its colour from `data[last] >= data[0]` alone, so an un-enveloped wander of
 * ±1.4% ended two of these curves below where they started while `net_pct` said `+1.9%` — a green
 * number over a red line, on the column whose whole job is showing the shape behind the number.
 * `sin(πt)` pins both endpoints to the drift, so direction and return cannot disagree. Caught by
 * looking at the render, which is the entire reason this preview exists.
 */
function curve(drift: number, wobble: number, phase: number, points = 30): string[] {
  const out: string[] = [];
  for (let i = 0; i < points; i += 1) {
    const t = i / (points - 1);
    const wander = Math.sin(i / 3.1 + phase) * 0.6 + Math.sin(i / 7.3 + phase) * 0.4;
    const envelope = Math.sin(Math.PI * t);
    out.push(
      (2_000 * (1 + (drift / 100) * t + (wobble / 100) * wander * envelope)).toFixed(2),
    );
  }
  return out;
}

/**
 * S14, and every significance state the rule can produce.
 *
 * ⚠ **The derived fields are computed by hand from `jmt_api.store.fleet`'s own rule, not invented
 * to look plausible.** `MIN_TRADES = 100`, `MIN_DAYS = 28`, `MIN_INSTRUMENTS = 10`,
 * `DIVERGENCE_LIMIT_SIGMA = 2`, and `confidence_pct = round((1 − |σ| / 2) × 99)`. A fixture whose
 * `significance` disagrees with its own numbers would let a broken rule render correctly here.
 *
 * Twenty candidates against D65's capacity of 168, so `20/168` exercises the below-capacity line
 * the way it will actually be seen — and it will be seen, because the real cohort is seeded in one
 * go while this preview stays small on purpose. And per this file's standing rule the set is
 * deliberately awkward — one flagged, one negative, one four-bar newcomer with no Sharpe and no
 * sparkline, one halted and one stopped row for `DataTable`'s dimming.
 *
 * 🔴 **Eleven of the twenty are one family across both live rungs**, swept on two dimensions on
 * `4h` and one on `1h`, and they are the only part of this fixture that exercises §8's
 * *neighbourhood* reading. The other nine are a collection, and a screen that grouped nothing would
 * look correct against them — so without the sweep this fixture would be a specification with a
 * hole in it.
 *
 * ⚠ **`total` and `promotable` are the API's own counts and are stated, not derived here.** They
 * must be kept in step with the rows by hand: the screen renders the API's figure precisely so that
 * *"the number on the screen and the number in the record cannot drift"*, which also means a
 * fixture that miscounts is a fixture that hides a drift rather than one that shows it.
 */
/**
 * The four fields S14 tasks 4.1 and 4.3 added, as one spread per fixture row.
 *
 * ⚠ **`bars_expected` is DERIVED here rather than typed per row**, and that is deliberate: the
 * server derives it from `Timeframe.seconds` over `days`, and a fixture holding its own literal is
 * a fixture that can disagree with the thing it stands in for. A shortfall must therefore be
 * expressed by lowering `bars_evaluated`, never by inventing a smaller `bars_expected`.
 *
 * 🔴 **`regime === null` and `[0, 0, 0]` are different fixtures on purpose.** `null` is *"the
 * classifier has not run"* and renders as an em dash; three zeroes are a real measurement and
 * render as three empty tracks. Deployments 24 and 25 are that pair, adjacent in the list, so the
 * screen shows the discrimination rather than one side of it.
 *
 * The keys are the classifier's own — `uptrend` / `downtrend` / `range`, per
 * `jmt_research.regime.REGIMES`.
 */
function evidence(
  timeframe: string,
  days: number,
  barsEvaluated: number,
  regime: readonly [number, number, number] | null,
): Pick<FleetCandidate, 'bars_evaluated' | 'bars_expected' | 'regime_counts' | 'regime_minimum'> {
  const perDay: Record<string, number> = { '15m': 96, '1h': 24, '4h': 6, '1d': 1 };
  return {
    bars_evaluated: barsEvaluated,
    bars_expected: days * (perDay[timeframe] ?? 24),
    regime_counts:
      regime === null ? null : { uptrend: regime[0], downtrend: regime[1], range: regime[2] },
    // §2's threshold. The screen reads it off the payload rather than holding its own copy.
    regime_minimum: 20,
  };
}

const FLEET: FleetResponse = {
  total: 22,
  capacity: 293,
  promotable: 10, promotable_unresolved: 2,
  flagged: 1,
  days: 47,
  // 🔴 **Cohort 4 §7 — net versus gross, and the fixture is deliberately NOT market-neutral.**
  // Gross €1,240 against a net €1,050 is 85% directional, which is the healthy reading and the one
  // the gallery should show as normal. ⚠ A fixture sitting at 0% would photograph the *warning*
  // state permanently and teach the eye that amber is the resting colour of this stat.
  gross_exposure: '1240.00',
  net_exposure: '1050.00',
  directional_pct: '84.7',
  candidates: [
    // ── 🔴 A real SWEEP, and it is the fixture the neighbourhood view needs ───────────────────
    //
    // `Fleet-Selection-Rule.md` §8 reads the cohort as neighbourhoods rather than as rows: *"if a
    // family passes at three adjacent points on a dimension, that is a neighbourhood and the result
    // is unlikely to be luck. If it passes at exactly one point and fails both neighbours, that is
    // noise wearing a winner's badge."*
    //
    // 🔴 **So the fixture carries one of each, in the same family**, because the grouping's whole
    // job is telling them apart and a fixture with only the good shape cannot show it doing so:
    //
    //     fast_period   ✓ ✓ ✓ ⧗   passing at 7 / 10 / 15, base 20   a NEIGHBOURHOOD
    //     slow_period   ⧗ ✓ ⧗ –   passing at 45 alone, base 60      ISOLATED — a non-result
    //
    // The two runs share a base (`fast_period` 20, `slow_period` 60, id 30), which is what makes
    // each run four points long off three variants — §8's `1 + d × (p − 1)`. It is also what lets
    // the screen identify the default at all: with a single swept dimension every candidate is one
    // step from every other, all four are hubs, and the `•` correctly goes unmarked.
    //
    // ⚠ **The base deliberately does not pass.** The base is a point on *every* dimension, so a
    // passing base would put an adjacent pass into both runs and leave no isolated point to render.
    //
    // ⚠ **The rest of this fixture is a collection, not a sweep**, and that is the specification
    // hole this block closes: without a same-family sweep, a screen that grouped nothing would look
    // correct against every other fixture in the file. `candidate` is the family name at D65 (26
    // rows read `DipBuyer`), so grouping is what makes 168 rows readable at all.
    { deployment_id: 30, candidate: 'TrendFollower', parameters: { fast_period: 20, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 10, book: 'core', days: 47, trades: 92, closes_by_reason: { signal: 71, stop: 19, take_profit: 2 }, open_positions: 2, held: ['BTC/EUR', 'ETH/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '0.20', last_entry_at: '2026-08-06T12:00:00Z', last_exit_at: '2026-08-06T09:10:00Z',
      net_pct: '1.40', sharpe: '0.52', max_drawdown_pct: '5.90', divergence_sigma: '0.20',
      significance: 'too_early', confidence_pct: null, blocked_by: '92/100 trades',
      ...evidence('4h', 47, 282, [35, 29, 22]),
      sparkline: curve(1.4, 1.1, 2.2) },
    { deployment_id: 31, candidate: 'TrendFollower', parameters: { fast_period: 7, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 121, closes_by_reason: { signal: 60, stop: 58, take_profit: 3 }, open_positions: 1, held: ['ETH/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '1.70', last_entry_at: '2026-08-06T13:01:00Z', last_exit_at: '2026-08-06T10:11:00Z',
      net_pct: '2.90', sharpe: '1.02', max_drawdown_pct: '4.80', divergence_sigma: '0.20',
      significance: 'promotable', confidence_pct: '89', blocked_by: null,
      ...evidence('4h', 47, 268, [34, 28, 9]),
      sparkline: curve(2.9, 0.9, 0.4) },
    { deployment_id: 32, candidate: 'TrendFollower', parameters: { fast_period: 10, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 134, closes_by_reason: { signal: 134 }, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '0.90', last_entry_at: '2026-08-06T14:02:00Z', last_exit_at: '2026-08-06T11:12:00Z',
      net_pct: '2.10', sharpe: '0.79', max_drawdown_pct: '5.10', divergence_sigma: '0.10',
      significance: 'promotable', confidence_pct: '94', blocked_by: null,
      ...evidence('4h', 47, 282, [41, 33, 22]),
      sparkline: curve(2.1, 1.0, 1.1) },
    { deployment_id: 33, candidate: 'TrendFollower', parameters: { fast_period: 15, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 127, closes_by_reason: { signal: 90, stop: 30, take_profit: 5, risk_engine: 2 }, open_positions: 3, held: ['ADA/EUR', 'LINK/EUR', 'AVAX/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '1.35', last_entry_at: '2026-08-06T15:03:00Z', last_exit_at: '2026-08-06T12:13:00Z',
      net_pct: '2.55', sharpe: '0.91', max_drawdown_pct: '4.60', divergence_sigma: '0.40',
      significance: 'promotable', confidence_pct: '79', blocked_by: null,
      ...evidence('4h', 47, 282, [38, 31, 24]),
      sparkline: curve(2.55, 0.9, 0.7) },
    // The second dimension. 🔴 45 passes and both its neighbours fail, which is exactly the shape
    // §8 refuses to read as a result — and on the flat table it is an indistinguishable `✓ 84%`.
    { deployment_id: 34, candidate: 'TrendFollower', parameters: { fast_period: 20, slow_period: 30, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 9, book: 'core', days: 47, trades: 88, closes_by_reason: {}, open_positions: 1, held: ['LINK/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-0.80', last_entry_at: '2026-08-06T16:04:00Z', last_exit_at: '2026-08-06T13:14:00Z',
      net_pct: '0.40', sharpe: '0.11', max_drawdown_pct: '7.20', divergence_sigma: '-0.40',
      significance: 'too_early', confidence_pct: null, blocked_by: '88/100 trades',
      ...evidence('4h', 47, 282, [33, 27, 21]),
      sparkline: curve(0.4, 1.2, 2.9) },
    { deployment_id: 35, candidate: 'TrendFollower', parameters: { fast_period: 20, slow_period: 45, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 118, closes_by_reason: {}, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '2.10', last_entry_at: '2026-08-06T17:05:00Z', last_exit_at: '2026-08-06T09:10:00Z',
      net_pct: '3.30', sharpe: '1.21', max_drawdown_pct: '4.10', divergence_sigma: '0.30',
      significance: 'promotable', confidence_pct: '84', blocked_by: null,
      ...evidence('4h', 47, 282, [45, 37, 28]),
      sparkline: curve(3.3, 1.0, 0.6) },
    { deployment_id: 36, candidate: 'TrendFollower', parameters: { fast_period: 20, slow_period: 68, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 145, closes_by_reason: {}, open_positions: 2, held: ['DOGE/EUR', 'BTC/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-2.05', last_entry_at: '2026-08-06T12:00:00Z', last_exit_at: '2026-08-06T10:11:00Z',
      net_pct: '-0.85', sharpe: '-0.22', max_drawdown_pct: '8.30', divergence_sigma: '-0.70',
      significance: 'not_ranked', confidence_pct: null,
      blocked_by: 'net return is not positive',      ...evidence('4h', 47, 282, [55, 45, 35]),
 sparkline: curve(-0.85, 1.3, 3.8) },
    // ── 🔴 The SAME SWEEP on the second rung, and it closes the last hole in this fixture ──────
    //
    // §8 repeats every sweep on both live timeframes and the spec is explicit about why: *"the
    // timeframe pair sits side by side, since a neighbourhood holding on both is the stronger
    // evidence."* Until this block existed **that layout had never been rendered** — every
    // TrendFollower row was `4h`, so the side-by-side grid was a single column and the reviewer
    // was looking at a degenerate case of it.
    //
    // Two properties are deliberate:
    //
    // 🔴 **`1h` is a PAIR where `4h` is a neighbourhood**, so the two rungs disagree. A fixture
    // that passed on both would render the layout without ever showing what it is for — the
    // comparison only means something if a rung can fail to hold.
    //
    // ⚠ **`slow_period` is swept on `4h` only**, which leaves the `1h` cell empty and exercises
    // the `—` that the grid draws there. That em dash is a *seeding* question — §8 repeats each
    // sweep on both rungs — and it is shown rather than tidied away for the same reason an
    // unswept candidate is listed rather than dropped.
    //
    // The base is **not identifiable here and must not be**: four candidates differing on one
    // key are all one step from each other, so every one is a hub, `baseOf` correctly returns
    // `null`, and no `•` is drawn. `FleetGrouping`'s docstring calls this out — *"picking the
    // first, or the modal value, would print a `•` that no data supports"* — and this is the
    // fixture that renders it.
    //
    //     1h  fast_period   ✓ ✓ ⧗ ⧗   passing at 7 / 10        a PAIR, not a neighbourhood
    //     1h  slow_period   —         not swept on this rung
    { deployment_id: 40, candidate: 'TrendFollower', parameters: { fast_period: 7, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 214, closes_by_reason: {}, open_positions: 1, held: ['BTC/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '0.75', last_entry_at: '2026-08-06T13:01:00Z', last_exit_at: '2026-08-06T11:12:00Z',
      net_pct: '1.95', sharpe: '0.68', max_drawdown_pct: '5.40', divergence_sigma: '0.30',
      significance: 'promotable', confidence_pct: '84', blocked_by: null,
      ...evidence('1h', 47, 1128, [81, 66, 51]),
      sparkline: curve(1.95, 1.0, 1.3) },
    { deployment_id: 41, candidate: 'TrendFollower', parameters: { fast_period: 10, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 236, closes_by_reason: {}, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '1.00', last_entry_at: '2026-08-06T14:02:00Z', last_exit_at: '2026-08-06T12:13:00Z',
      net_pct: '2.20', sharpe: '0.74', max_drawdown_pct: '5.05', divergence_sigma: '0.10',
      significance: 'promotable', confidence_pct: '94', blocked_by: null,
      ...evidence('1h', 47, 1128, [90, 73, 57]),
      sparkline: curve(2.2, 1.0, 0.9) },
    { deployment_id: 42, candidate: 'TrendFollower', parameters: { fast_period: 15, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 8, book: 'core', days: 47, trades: 74, closes_by_reason: {}, open_positions: 3, held: ['SOL/EUR', 'ADA/EUR', 'LINK/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-0.25', last_entry_at: '2026-08-06T15:03:00Z', last_exit_at: '2026-08-06T13:14:00Z',
      net_pct: '0.95', sharpe: '0.31', max_drawdown_pct: '6.10', divergence_sigma: '0.50',
      significance: 'too_early', confidence_pct: null, blocked_by: '74/100 trades',
      ...evidence('1h', 47, 1128, [28, 23, 18]),
      sparkline: curve(0.95, 1.1, 1.8) },
    { deployment_id: 43, candidate: 'TrendFollower', parameters: { fast_period: 20, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 6, book: 'core', days: 47, trades: 61, closes_by_reason: {}, open_positions: 1, held: ['ADA/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-0.60', last_entry_at: '2026-08-06T16:04:00Z', last_exit_at: '2026-08-06T09:10:00Z',
      net_pct: '0.60', sharpe: '0.19', max_drawdown_pct: '6.80', divergence_sigma: '0.40',
      significance: 'too_early', confidence_pct: null, blocked_by: '61/100 trades',
      ...evidence('1h', 47, 1128, [23, 19, 15]),
      sparkline: curve(0.6, 1.2, 2.4) },
    // ── Cohort 4 §5/§7: the states that did not exist before shorts ────────────────────────────
    //
    // 🔴 **These two are here because the populated screen only exists in this gallery.** The
    // `research` mark, the short caret and a mixed long/short holding are unreachable from
    // `ops/screenshot_ui.py`, whose docstring records that it photographs S14's **empty** state — so
    // without these fixtures the three §7 changes would ship unphotographed, which is how the hero
    // tile spent five sessions three type steps too small.
    //
    // 🔴 **D73: `promotable` here, and it is now the API's own answer.** §4 returned `not_ranked`
    // for any short before it looked at the minimums; this fixture said so, and the mark it produced
    // was an enclosed *dash*. Shorts now rank, so the fixture has to exercise the state the change
    // created — otherwise the preview shows five marks and the sixth exists only in a unit test.
    //
    // ⚠ **`excess_pct` is what earns it, not `net_pct`.** +2.60% against a `sell_and_hold` control
    // that returned +1.05% is +1.55% of excess — a short that genuinely beat holding the short, which
    // in a 58-of-74-downtrend window is the only version of that claim worth anything.
    { deployment_id: 60, candidate: 'TrendFollowerShort', parameters: { fast_period: 10, slow_period: 60, stop_pct: '8', risk_pct: '0.16' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 74, instruments_traded: 31, book: 'core', days: 47, trades: 142, closes_by_reason: {}, open_positions: 2,
      // 🔴 **EIGHT held, every one of them short — the shape the live fleet's `SellAndHold` controls
      // are in, and the one that clipped `+N` into the `Age` column.** A short costs ~28px in the
      // Holding cell against a long's ~11, so this is the widest that cell can ever be asked to be.
      // The fixture had two shorts and therefore could not reproduce the operator's screenshot.
      held: ['SOL/EUR', 'ADA/EUR', 'BTC/EUR', 'ETH/EUR', 'SEI/EUR', 'W/EUR', 'LINK/EUR', 'AVAX/EUR'],
      held_short: ['SOL/EUR', 'ADA/EUR', 'BTC/EUR', 'ETH/EUR', 'SEI/EUR', 'W/EUR', 'LINK/EUR', 'AVAX/EUR'],
      execution_unresolved: true, direction: 'short', control_pct: '1.05', excess_pct: '1.55',
      last_entry_at: '2026-08-06T13:01:00Z', last_exit_at: '2026-08-06T11:12:00Z',
      net_pct: '2.60', sharpe: '0.81', max_drawdown_pct: '4.90', divergence_sigma: '0.40',
      significance: 'promotable', confidence_pct: '80',
      blocked_by: 'promotable, but the venue and Box 1 decisions come first: OKX perps are USDT-margined and this account is spot-only (D73)',
      ...evidence('4h', 47, 282, [22, 45, 18]),
      sparkline: curve(2.6, 1.0, 1.1) },
    // Recipe 4 holds BOTH sides at once, which no other family does — so it is the row that shows
    // the caret discriminating rather than merely being present. Shorts sort first, so `SEI` and
    // `W` lead and the two longs follow.
    { deployment_id: 61, candidate: 'CrossSectionalMomentum', parameters: { lookback_period: 180, hold_count: 2, rebalance_bars: 42, size_pct: '4' },
      version: 1, state: 'running',
      timeframe: '4h', instruments: 74, instruments_traded: 44, book: 'core', days: 47, trades: 88, closes_by_reason: {}, open_positions: 4,
      held: ['BTC/EUR', 'ETH/EUR', 'SEI/EUR', 'W/EUR'], held_short: ['SEI/EUR', 'W/EUR'], execution_unresolved: true, direction: 'neutral', control_pct: '0', excess_pct: '0.40',
      last_entry_at: '2026-08-06T12:00:00Z', last_exit_at: '2026-08-05T12:00:00Z',
      net_pct: '0.40', sharpe: '0.22', max_drawdown_pct: '3.10', divergence_sigma: null,
      significance: 'promotable', confidence_pct: null,
      blocked_by: 'divergence-exempt: declares extra timeframes, which HistoricalFeed cannot replay, so this family has no backtest to compare against in any cohort; promotable, but the venue and Box 1 decisions come first — and this is the market-neutral family, so Box 1 is not hypothetical (D73)',
      ...evidence('4h', 47, 282, [19, 24, 15]),
      sparkline: curve(0.4, 0.8, 0.9) },
    // ── The pre-D65 collection, kept because every significance state below is still needed ────
    { deployment_id: 12, candidate: 'trend-follower', parameters: {}, version: 3, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 118, closes_by_reason: {}, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '1.94', last_entry_at: '2026-08-06T17:05:00Z', last_exit_at: '2026-08-06T10:11:00Z',
      net_pct: '3.14', sharpe: '1.14', max_drawdown_pct: '4.23', divergence_sigma: '0.30',
      significance: 'promotable', confidence_pct: '84', blocked_by: null,
      ...evidence('1h', 47, 1128, [44, 37, 26]),
      sparkline: curve(3.14, 0.9, 0.0) },
    { deployment_id: 7, candidate: 'dip-buyer-in-uptrend', parameters: {}, version: 2, state: 'running',
      timeframe: '4h', instruments: 11, instruments_traded: 11, book: 'core', days: 44, trades: 103, closes_by_reason: {}, open_positions: 2, held: ['AVAX/EUR', 'DOGE/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '0.66', last_entry_at: '2026-08-06T12:00:00Z', last_exit_at: '2026-08-06T11:12:00Z',
      net_pct: '1.86', sharpe: '0.71', max_drawdown_pct: '6.90', divergence_sigma: '0.90',
      significance: 'promotable', confidence_pct: '54', blocked_by: null,
      ...evidence('4h', 44, 264, [36, 29, 21]),
      sparkline: curve(1.86, 1.4, 1.7) },
    // Flagged BEFORE the minimums are checked, and it clears all three of them — which is the
    // point: a candidate can be fully qualified and still be a bug report.
    { deployment_id: 21, candidate: 'vol-breakout', parameters: {}, version: 5, state: 'running',
      timeframe: '15m', instruments: 14, instruments_traded: 14, book: 'core', days: 47, trades: 341, closes_by_reason: {}, open_positions: 1, held: ['DOGE/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-3.61', last_entry_at: '2026-08-06T13:01:00Z', last_exit_at: '2026-08-06T12:13:00Z',
      net_pct: '-2.41', sharpe: '0.12', max_drawdown_pct: '9.08', divergence_sigma: '-2.90',
      significance: 'flagged', confidence_pct: null,
      blocked_by: '2.9σ below its own backtest — a bug signal, not a result',
      ...evidence('15m', 47, 4512, [60, 40, 12]),
      sparkline: curve(-2.41, 2.2, 3.4) },
    { deployment_id: 15, candidate: 'cross-sectional-momentum', parameters: {}, version: 1, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 12, book: 'core', days: 47, trades: 156, closes_by_reason: {}, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-2.32', last_entry_at: '2026-08-06T14:02:00Z', last_exit_at: '2026-08-06T13:14:00Z',
      net_pct: '-1.12', sharpe: '-0.34', max_drawdown_pct: '7.71', divergence_sigma: '-1.20',
      significance: 'not_ranked', confidence_pct: null,
      blocked_by: 'net return is not positive',      ...evidence('1h', 47, 1128, [47, 39, 27]),
 sparkline: curve(-1.12, 1.1, 5.2) },
    { deployment_id: 9, candidate: 'trend-follower-regime-filter', parameters: {}, version: 2, state: 'running',
      timeframe: '1h', instruments: 12, instruments_traded: 6, book: 'core', days: 47, trades: 62, closes_by_reason: {}, open_positions: 3, held: ['ETH/EUR', 'SOL/EUR', 'ADA/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '0.85', last_entry_at: '2026-08-06T15:03:00Z', last_exit_at: '2026-08-06T09:10:00Z',
      net_pct: '2.05', sharpe: '0.88', max_drawdown_pct: '5.40', divergence_sigma: '-0.60',
      significance: 'too_early', confidence_pct: null, blocked_by: '62/100 trades',
      ...evidence('1h', 47, 1128, [24, 19, 15]),
      sparkline: curve(2.05, 1.0, 2.6) },
    { deployment_id: 18, candidate: 'rsi-reversion', parameters: {}, version: 4, state: 'running',
      timeframe: '15m', instruments: 4, instruments_traded: 4, book: 'core', days: 47, trades: 214, closes_by_reason: {}, open_positions: 1, held: ['SOL/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-0.26', last_entry_at: '2026-08-06T16:04:00Z', last_exit_at: '2026-08-06T10:11:00Z',
      net_pct: '0.94', sharpe: '0.41', max_drawdown_pct: '3.60', divergence_sigma: '0.20',
      significance: 'too_early', confidence_pct: null, blocked_by: '4/10 instruments',
      ...evidence('15m', 47, 4512, [81, 64, 38]),
      sparkline: curve(0.94, 0.8, 4.1) },
    // Four bars old. Everything a return series is needed for is null, and `Num` must render an
    // em dash in all of them — `0.00` here would be a number where there is no measurement.
    { deployment_id: 24, candidate: 'mean-reversion-alt', parameters: {}, version: 1, state: 'starting',
      timeframe: '1h', instruments: 8, instruments_traded: 0, book: 'core', days: 1, trades: 0, closes_by_reason: {}, open_positions: 0, held: [], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '0', excess_pct: null, last_entry_at: null, last_exit_at: null,
      net_pct: null, sharpe: null, max_drawdown_pct: null, divergence_sigma: null,
      significance: 'too_early', confidence_pct: null,
      blocked_by: '0/100 trades, 1/28 days, 8/10 instruments',      ...evidence('1h', 1, 0, null),
 sparkline: [] },
    // 🔴 **Zero closed trades while holding three positions**, which is the shape the whole live
    // fleet was in on 2026-08-06 — 235 open, 0 closed, across 102 deployments — and which the
    // table could not distinguish from the row above until `open_positions` existed. Both rows
    // are kept precisely because the pair is the test: `0t` and `0t +3 open` must not look alike.
    { deployment_id: 25, candidate: 'mean-reversion-alt', parameters: {}, version: 1, state: 'running',
      timeframe: '1h', instruments: 9, instruments_traded: 0, book: 'core', days: 1, trades: 0, closes_by_reason: {}, open_positions: 3, held: ['LINK/EUR', 'AVAX/EUR', 'DOGE/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-1.10', last_entry_at: '2026-08-06T12:00:00Z', last_exit_at: null,
      net_pct: '0.10', sharpe: null, max_drawdown_pct: '0.20', divergence_sigma: null,
      significance: 'too_early', confidence_pct: null,
      blocked_by: '0/100 trades, 1/28 days, 9/10 instruments',      ...evidence('1h', 1, 24, [0, 0, 0]),
 sparkline: curve(0.1, 0.2, 0.1) },
    // Not participating: `DataTable` dims both, and the rows stay readable because the archive of
    // failures is worth keeping.
    { deployment_id: 4, candidate: 'donchian-channel', parameters: {}, version: 4, state: 'halted',
      timeframe: '1h', instruments: 12, instruments_traded: 12, book: 'core', days: 31, trades: 129, closes_by_reason: {}, open_positions: 2, held: ['AVAX/EUR', 'DOGE/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '-1.82', last_entry_at: '2026-08-06T13:01:00Z', last_exit_at: '2026-08-06T13:14:00Z',
      net_pct: '-0.62', sharpe: '-0.18', max_drawdown_pct: '11.40', divergence_sigma: '-1.60',
      significance: 'not_ranked', confidence_pct: null,
      blocked_by: 'net return is not positive',      ...evidence('1h', 31, 604, [29, 26, 14]),
 sparkline: curve(-0.62, 1.8, 6.0) },
    { deployment_id: 2, candidate: 'buy-and-hold', parameters: {}, version: 1, state: 'stopped',
      timeframe: '1d', instruments: 12, instruments_traded: 1, book: 'core', days: 47, trades: 12, closes_by_reason: {}, open_positions: 1, held: ['DOGE/EUR'], held_short: [], execution_unresolved: false, direction: 'long', control_pct: '1.20', excess_pct: '4.60', last_entry_at: '2026-08-06T14:02:00Z', last_exit_at: '2026-08-06T09:10:00Z',
      net_pct: '5.80', sharpe: '0.63', max_drawdown_pct: '14.20', divergence_sigma: '0.10',
      significance: 'too_early', confidence_pct: null, blocked_by: '12/100 trades',
      ...evidence('1d', 47, 47, [24, 18, 5]),
      sparkline: curve(5.8, 2.6, 0.9) },
  ],
};

const SCREENS = [
  { value: 'markets', label: 'Markets' },
  { value: 'system', label: 'System' },
  { value: 'fleet', label: 'Fleet' },
  { value: 'research', label: 'Research' },
  { value: 'screener', label: 'Screener' },
  { value: 'growth', label: 'Growth' },
  { value: 'company', label: 'Company' },
  { value: 'news', label: 'News' },
  { value: 'scorecard', label: 'Scorecard' },
  { value: 'coverage', label: 'Coverage' },
  { value: 'trump', label: 'Trump Watch' },
];

const BARS = bars(320, Math.floor(Date.parse('2026-07-19T04:00:00Z') / 1000), 3_600);

/** Path → fixture. Matched on `pathname` only, so query strings are ignored: `/bars` is the
    same answer whatever instrument and timeframe are asked for, which is what makes the
    instrument and timeframe selectors usable on the preview. */
/**
 * The executions pane's fixture, and it is **deliberately the awkward case**.
 *
 * 🔴 **`AIDOGE/EUR` at `0.0000000262` is the row this fixture exists for.** Roughly seventy of
 * this venue's seventy-seven tradeable pairs price in that range, and the obvious implementation —
 * every number through `Num` — renders it as **€0.00**, because `formatNum` fixes 2dp and calls
 * `Math.abs`. Putting it in the same table as BTC at five figures is what makes the column's
 * problem visible at a glance instead of six weeks in, on one instrument.
 *
 * The rest carries one of each state the pane can show: an entry and an exit, a maker and a
 * taker, and one `gapped` fill — *"a stop that 'worked' on a gap did not work"*.
 *
 * ⚠ **`total` exceeds the returned rows on purpose**, so the truncation line renders. A fixture
 * where the page is the whole record would leave that sentence untested by eye, and it is the one
 * sentence standing between a partial table and a reader who thinks it is complete.
 */
/**
 * One candidate's positions, for S14 task 3's fourth pane.
 *
 * 🔴 **Every lane state the chart can draw is here, because the populated pane only exists in this
 * gallery.** `ops/screenshot_ui.py` photographs S14's *empty* state, so a state absent from this
 * fixture ships unphotographed — which is how the hero tile spent five sessions three type steps too
 * small. So: a long closed by signal, a long closed by a stop, a short closed by a stop, a short
 * closed by signal, an open long and an open short. Six lanes, six renderings.
 *
 * ⚠ **Two of them are OPEN, with `closed_at`, `exit_reason` and `realised_gross` all `null`.** That
 * triple is what the chart reads to draw an arrowhead at the now-rule instead of a terminal, and
 * `realised_gross: null` rather than `"0"` is the distinction the store exists to make — the column
 * itself is `not null default 0` and cannot express it.
 *
 * ⚠ **The timestamps are relative to load**, not fixed dates. A pinned date drifts out of the axis
 * as the calendar moves, which is the trap `test_api_fleet.py` records for its own fixture: the
 * sparkline came back empty because the fixture was two months stale and the code was right.
 */
const FLEET_POSITIONS: FleetPositionsResponse = (() => {
  const day = 86_400_000;
  const now = Date.now();
  const at = (daysAgo: number): string => new Date(now - daysAgo * day).toISOString();
  return {
    // Newest first, as the API returns them.
    positions: [
      { position_id: 1848, symbol: 'ADA/EUR', side: 'short', opened_at: at(4),
        closed_at: null, exit_reason: null, realised_gross: null, bars_held: 24 },
      { position_id: 1847, symbol: 'DOGE/EUR', side: 'long', opened_at: at(9),
        closed_at: null, exit_reason: null, realised_gross: null, bars_held: 54 },
      { position_id: 1846, symbol: 'AVAX/EUR', side: 'short', opened_at: at(16),
        closed_at: at(12), exit_reason: 'signal', realised_gross: '11.40', bars_held: 24 },
      { position_id: 1845, symbol: 'ETH/EUR', side: 'long', opened_at: at(22),
        closed_at: at(7), exit_reason: 'signal', realised_gross: '63.10', bars_held: 90 },
      { position_id: 1844, symbol: 'SOL/EUR', side: 'short', opened_at: at(25),
        closed_at: at(21), exit_reason: 'stop', realised_gross: '-22.05', bars_held: 24 },
      { position_id: 1843, symbol: 'LINK/EUR', side: 'long', opened_at: at(33),
        closed_at: at(27), exit_reason: 'stop', realised_gross: '-18.50', bars_held: 36 },
      { position_id: 1842, symbol: 'BTC/EUR', side: 'long', opened_at: at(38),
        closed_at: at(20), exit_reason: 'signal', realised_gross: '96.20', bars_held: 108 },
      { position_id: 1841, symbol: 'ADA/EUR', side: 'long', opened_at: at(41),
        closed_at: at(34), exit_reason: 'signal', realised_gross: '41.20', bars_held: 42 },
    ],
    truncated: false,
  };
})();

const FLEET_FILLS: FleetFillsResponse = {
  total: 214,
  returned: 7,
  fills: [
    {
      ts: '2026-08-08T04:00:11Z', symbol: 'BTC/EUR', side: 'sell', reduce_only: true,
      quantity: '0.00413000', price: '101457.30', notional: '419.0212345600000000', fee: '0.419021234560000',
      liquidity: 'taker', gapped: true,
    },
    // 🔴 **An F-D84 phantom, and it is in this fixture because the Side column exists for it.**
    // A `reduce_only` BUY: a closing fill applied to an already-flat book, which `PositionBook`
    // reads as an *opening* fill. Live on 2026-08-08 as dep 297's position 4900 on ADA/EUR. Every
    // other row here agrees entry⇒buy / exit⇒sell, so this is the only one that prints its side —
    // which is the behaviour being previewed, not just a spare row.
    {
      ts: '2026-08-08T03:30:00Z', symbol: 'ADA/EUR', side: 'buy', reduce_only: true,
      quantity: '163.09710000', price: '0.1708573011', notional: '27.8613219241100000', fee: '0.027861321924110',
      liquidity: 'taker', gapped: false,
    },
    {
      ts: '2026-08-08T01:00:07Z', symbol: 'AIDOGE/EUR', side: 'buy', reduce_only: false,
      quantity: '9541984732.00000000', price: '0.0000000262', notional: '250.0000000000000000', fee: '0.250000000000000',
      liquidity: 'maker', gapped: false,
    },
    {
      ts: '2026-08-07T22:00:44Z', symbol: 'ADA/EUR', side: 'sell', reduce_only: true,
      quantity: '163.09710000', price: '0.1708573011', notional: '27.8613219241100000', fee: '0.027861321924110',
      liquidity: 'taker', gapped: false,
    },
    {
      ts: '2026-08-07T19:00:02Z', symbol: 'ETH/EUR', side: 'buy', reduce_only: false,
      quantity: '0.08120000', price: '3079.44', notional: '250.0512345678900000', fee: '0.250051234567890',
      liquidity: 'maker', gapped: false,
    },
    {
      ts: '2026-08-07T15:00:38Z', symbol: 'ADA/EUR', side: 'buy', reduce_only: false,
      quantity: '163.09710000', price: '0.1769146945', notional: '28.8512349999900000', fee: '0.000569210295060',
      liquidity: 'taker', gapped: false,
    },
    {
      ts: '2026-08-07T11:00:00Z', symbol: 'BTC/EUR', side: 'buy', reduce_only: false,
      quantity: '0.00413000', price: '99812.05', notional: '412.2176117440000000', fee: '0.412217611744000',
      liquidity: 'maker', gapped: false,
    },
  ],
  request_id: 'preview',
};

/** The fleet-wide history: the same fills, with the candidate that made each one. */
const FLEET_HISTORY = {
  total: 1482,
  returned: FLEET_FILLS.fills.length,
  fills: FLEET_FILLS.fills.map((fill, index) => ({
    ...fill,
    deployment_id: 297 + index,
    timeframe: ['1h', '4h', '1h', '4h'][index % 4],
    candidate: ['VolatilityBreakout', 'DipBuyer', 'TrendFollower', 'TrailingStopRider'][index % 4],
  })),
  request_id: 'preview',
};

/* ── S5 Research ────────────────────────────────────────────────────────────────────
   🔴 **These fixtures reproduce the operator's report of 2026-08-23**, not a happy path.

   *"The dropdown in research does not work, every strategy gives the same numbers."* The
   dropdown was working. Six runs existed, **all of one strategy version**, and a `full` and a
   `walkforward` over the same range have the same headline by construction — the walk-forward
   adds folds, it does not replay differently. Three sweeps over one window are identical to each
   other for the same reason, because D75 makes a sweep's headline its baseline.

   So the numbers below are deliberately repeated: runs 1-3 all return 1.8246%, runs 4-6 all
   return 2.2669%. A preview that gave every run a different number would render beautifully and
   prove nothing about the thing that was actually wrong.

   ⚠ **This screen was not in the harness at all until now**, which is why the label defect
   survived: nothing rendered it without a backend, and reaching it on the real box needs a
   password and a code from a phone. */

/* 🔴 The names are the real ones, **including the two long ones**. `CrossSectionalMomentum` and
   `MultiTimeframeConfirmation` are what an option has to survive, and a fixture of short names
   would render a dropdown that fits and prove nothing. */
const RESEARCH_NAMES = [
  'BuyAndHold',
  'DipBuyer',
  'TrendFollower',
  'MultiTimeframeConfirmation',
  'RegimeFilteredTrend',
  'TimeStoppedReversion',
  'TrailingStopRider',
  'VolatilityBreakout',
  'CrossSectionalMomentum',
  'SellAndHold',
  'RegimeSwitchedTrend',
  'TrendFollowerShort',
  'VolatilityBreakdown',
  'BidirectionalBreakout',
];

/*
  Which way each family can trade, as `strategies.tags` carries it.

  🔴 **Three states, all of them on screen.** Long-only, short-only and *both* are genuinely
  different — D73 needs the both-ways case to be sayable, which is why `long` is not the absence of
  `short`. A fixture tagging everything one way would render a direction filter with one option and
  prove nothing about the other two.

  ⚠ **One entry is deliberately left untagged**, because untagged is a fourth state the table has
  to render: tags are written by `seed_strategies` from an AST parse of the source, so a strategy
  registered through the API and never seeded carries none.

  🔴 **Which entry is not arbitrary — it has to be one with a completed `full` run.** The table rows
  are runs, not strategies, so tagging a family the run fixture never covers leaves the branch
  looking exercised while nothing renders it. `MultiTimeframeConfirmation` was the first choice and
  was exactly that mistake: no `full` run, no row, no untagged label anywhere on screen.
*/
const RESEARCH_TAGS: Record<string, string[]> = {
  CrossSectionalMomentum: ['long', 'short'],
  RegimeSwitchedTrend: ['long', 'short'],
  BidirectionalBreakout: ['long', 'short'],
  SellAndHold: ['short'],
  TrendFollowerShort: ['short'],
  VolatilityBreakdown: ['short'],
  VolatilityBreakout: [],
};

const RESEARCH_STRATEGIES = {
  strategies: RESEARCH_NAMES.map((name, index) => ({
    id: index + 1,
    name,
    description: null,
    state: StrategyState.DRAFT,
    current_version_id: index + 12,
    tags: RESEARCH_TAGS[name] ?? ['long'],
    version_count: 1,
    created_at: '2026-08-01T00:00:00Z',
    retired_at: null,
    retire_reason: null,
  })),
  total: RESEARCH_NAMES.length,
  request_id: 'preview',
};

/** The ten-symbol basket `CrossSectionalMomentum` needs — and what wrapped the trigger. */
const RESEARCH_BASKET = [
  'BTC/EUR',
  'ADA/EUR',
  'AAVE/EUR',
  'ALGO/EUR',
  'APT/EUR',
  'ARB/EUR',
  'AVAX/EUR',
  'AXS/EUR',
  'APE/EUR',
  'CHZ/EUR',
];

/*
  🔴 **Sharpe, drawdown and top-five share are derived, not pinned — and that is the point.** They
  were three constants here, so every row of the strategy table rendered `0.28 / 2.76% / 72.93%`.
  That is indistinguishable from the table reading one run's metrics for all thirteen rows, or from
  a column wired to the wrong key: a fixture that makes every row identical cannot reveal a bug in
  the columns it fills. `total_return_pct` and `trade_count` varied, which is why only those two
  columns were reviewable.

  ⚠ **Top-five share falls as trade count rises**, which is the relationship the real batch shows —
  and it crosses 50% at ~20 trades, so both sides of the threshold G11 refuses are on screen at
  once. A fixture that sat entirely on one side would leave the red colouring unproven.

  `extra` still wins, so a call site can pin an exact number when a screen is about that number.
*/
function researchMetrics(ret: string, trades: number, extra: Record<string, unknown> = {}) {
  const r = Number(ret);
  return {
    starting_capital: '10000',
    ending_equity: '10226.69',
    gross_pnl: '243.10',
    fees: '16.41',
    net_pnl: '226.69',
    total_return_pct: ret,
    cagr_pct: '1.13',
    volatility_pct: '4.02',
    downside_deviation_pct: '2.71',
    sharpe: (r * 0.31 + 0.09).toFixed(4),
    sortino: (r * 0.46 + 0.13).toFixed(4),
    calmar: '0.4098',
    var_95_pct: '-0.41',
    cvar_95_pct: '-0.62',
    max_drawdown_pct: (2.4 + Math.abs(r) * 1.7).toFixed(2),
    max_drawdown_recovered: true,
    longest_underwater_days: '320.00',
    trade_count: trades,
    win_rate_pct: '31.58',
    average_win: '38.12',
    average_loss: '-11.44',
    profit_factor: '1.61',
    expectancy: '1.48',
    longest_win_streak: 2,
    longest_loss_streak: 7,
    average_bars_held: '31.4',
    top_five_profit_share_pct: (95 / (1 + trades / 22)).toFixed(2),
    cost_share_of_gross_profit_pct: '6.75',
    time_in_market_pct: '41.2',
    days: '720.00',
    unrealised_pnl: '0',
    lookahead_parity: null,
    trades_per_regime: { uptrend: 10, downtrend: 19, range: 5, unclassified: 4 },
    ...extra,
  };
}

const RESEARCH_FOLDS = [0, 1, 2].map((index) => ({
  index,
  train_start: '2024-09-02T00:00:00Z',
  train_end: `2025-1${index}-01T00:00:00Z`,
  test_start: `2025-1${index}-01T00:00:00Z`,
  test_end: `2026-0${index + 1}-01T00:00:00Z`,
  opening_equity: '10000',
  bars: 540,
  trades: [12, 11, 12][index],
  metrics: researchMetrics(['0.91', '-0.34', '1.39'][index]!, [12, 11, 12][index]!),
}));

const RESEARCH_WALKFORWARD = {
  mode: 'anchored',
  folds_requested: 3,
  min_train_bars: 200,
  optimised: false,
  fitted: [],
  purge_bars: 200,
  embargo_bars: 0,
  contiguous: true,
  folds_positive: 2,
  g3_consistency_pct: '66.67',
  largest_fold_share_pct: '53.52',
  control_return_pct: '-6.37',
  combined: researchMetrics('1.96', 35),
  folds: RESEARCH_FOLDS,
};

const RESEARCH_BENCHMARK = {
  benchmark_return_pct: '-6.37',
  strategy_return_pct: '2.27',
  excess_return_pct: '8.64',
  benchmark_max_drawdown_pct: '31.02',
  strategy_wins: true,
};

const RESEARCH_WARNINGS = [
  {
    // ⚠ **The check name and the `limit` spelling are `health.py`'s**, not invented. The Benchmark
    // tile reads the control out of `limit` — that is where `_benchmark` records which of the
    // three D73 controls it actually used — so a fixture that omitted this check would exercise
    // only the fallback and leave the real path unrendered.
    name: 'beats_the_passive_control_in_this_window',
    health: 'ok',
    limit: 'above buy-and-hold',
    actual: '2.27% vs -6.37%',
    message: 'Ahead of the buy-and-hold control over this window — which is information, not a pass.',
  },
  {
    name: 'trade_count',
    health: 'warning',
    limit: '>= 30 trades',
    actual: '38',
    message: 'Thirty-eight trades is thin. Below a hundred the metrics describe a sample.',
  },
  {
    name: 'lookahead_parity',
    health: 'unknown',
    limit: 'pass',
    actual: 'not run',
    message: 'The parity check did not run. This is not a pass.',
  },
  {
    name: 'profit_concentration',
    health: 'warning',
    limit: '< 50.00%',
    actual: '72.93%',
    message: 'The five best trades produced 72.93% of the gross profit.',
  },
];

function researchRun(
  id: number,
  kind: string,
  ret: string,
  trades: number,
  from: string,
  to: string,
  extra: Record<string, unknown> = {},
  strategyId = 3,
  symbols: string[] = ['BTC/EUR'],
  state = 'done',
  error: string | null = null,
  timeframe = '4h',
) {
  return {
    id,
    strategy_id: strategyId,
    strategy_version_id: strategyId + 11,
    kind,
    range_start: `${from}T00:00:00Z`,
    range_end: `${to}T00:00:00Z`,
    symbols,
    timeframe,
    parameters: {},
    cost_model: {},
    run_config: null,
    seed: 1,
    state,
    metrics: state === 'done' ? researchMetrics(ret, trades, extra) : null,
    warnings: state === 'done' ? RESEARCH_WARNINGS : null,
    benchmark: state === 'done' ? RESEARCH_BENCHMARK : null,
    bars_replayed: kind === 'sweep' ? 1079 : 4319,
    intents: trades * 2,
    rejections: 0,
    started_at: `${from}T00:00:00Z`,
    finished_at: `${to}T00:00:00Z`,
    error,
  };
}

const LONG = ['2024-09-02', '2026-08-23'] as const;

/** Nineteen runs, newest first — the shape and the order `GET /backtests` returns. */
const RESEARCH_RUNS = {
  runs: [
    // The thirteen one-per-strategy runs, with the real numbers from the box.
    researchRun(19, 'full', '-4.7200', 79, ...LONG, {}, 14),
    researchRun(18, 'full', '-14.1600', 46, ...LONG, {}, 13),
    researchRun(17, 'full', '0.4100', 39, ...LONG, {}, 12),
    researchRun(16, 'full', '-14.3900', 45, ...LONG, {}, 11),
    researchRun(15, 'full', '-10.9300', 0, ...LONG, {}, 10),
    // 🔴 Ten symbols and a twenty-two character name. This is the option that broke the trigger.
    researchRun(14, 'full', '-1.3800', 178, ...LONG, {}, 9, RESEARCH_BASKET),
    researchRun(13, 'full', '-4.7200', 79, ...LONG, {}, 8),
    researchRun(12, 'full', '-14.3900', 45, ...LONG, {}, 7),
    researchRun(11, 'full', '-0.3600', 4, ...LONG, {}, 6),
    researchRun(10, 'full', '2.1800', 6, ...LONG, {}, 5),
    // ⚠ A failed run, because the list contains one and a report has to render it.
    researchRun(
      9,
      'full',
      '0',
      0,
      ...LONG,
      {},
      4,
      ['BTC/EUR'],
      'failed',
      'RunError: MultiTimeframeConfirmation declares extra_timeframes [\'4h\'] and a backtest ' +
        'replays one timeframe.',
    ),
    researchRun(8, 'full', '-1.5500', 3, ...LONG, {}, 2),
    researchRun(7, 'full', '4.7800', 0, ...LONG, {}, 1),
    // 🔴 And TrendFollower's six, which are the pairs that share a headline — three sweeps over one
    // window, then a full and two walk-forwards over another. That repetition is the fixture's
    // whole point: it is what "every strategy gives the same numbers" actually looked like.
    researchRun(6, 'walkforward', '2.2669', 38, ...LONG, { walkforward: RESEARCH_WALKFORWARD }),
    researchRun(5, 'walkforward', '2.2669', 38, ...LONG, { walkforward: RESEARCH_WALKFORWARD }),
    researchRun(4, 'full', '2.2669', 38, ...LONG),
    researchRun(3, 'sweep', '1.8246', 6, '2026-02-24', '2026-08-23'),
    researchRun(2, 'sweep', '1.8246', 6, '2026-02-24', '2026-08-23'),
    researchRun(1, 'sweep', '1.8246', 6, '2026-02-24', '2026-08-23'),
    /* 🔴 The same strategies at 1h and 1d, because a run of one strategy at three rungs differs
       **only** by the rung — which is exactly what the label and the filter have to make legible.
       A fixture with one timeframe cannot test either. */
    ...[1, 2, 3, 5, 6].flatMap((sid, n) => [
      researchRun(100 + n, 'walkforward', ['5.90', '-2.10', '3.44', '0.12', '1.77'][n]!, 140 + n,
        ...LONG, { walkforward: RESEARCH_WALKFORWARD }, sid, ['BTC/EUR'], 'done', null, '1h'),
      researchRun(200 + n, 'walkforward', ['1.02', '-0.44', '0.88', '-1.19', '2.31'][n]!, 9 + n,
        ...LONG, { walkforward: { ...RESEARCH_WALKFORWARD, reason: 'the range is too short to fold at 1d', folds: [] } },
        sid, ['BTC/EUR'], 'done', null, '1d'),
    ]),
  ],
  total: 29,
  request_id: 'preview',
};

const RESEARCH_TRADES = {
  run_id: 4,
  trades: [0, 1, 2, 3, 4].map((index) => ({
    id: index + 1,
    instrument_id: 1,
    side: 'buy',
    entry_ts: `2025-0${index + 1}-04T08:00:00Z`,
    entry_price: '52140.00',
    exit_ts: `2025-0${index + 1}-19T16:00:00Z`,
    exit_price: index % 2 === 0 ? '54980.00' : '50110.00',
    quantity: '0.00190',
    gross_pnl: index % 2 === 0 ? '54.02' : '-38.61',
    fees: '0.43',
    net_pnl: index % 2 === 0 ? '53.59' : '-39.04',
    exit_reason: index % 2 === 0 ? 'signal' : 'stop',
    mae: '41.10',
    mfe: '88.20',
  })),
  total: 5,
  request_id: 'preview',
};

/** 180 points, so the plot has a shape rather than a line between two dots. */
const RESEARCH_CURVE = Array.from({ length: 180 }, (_, index) => ({
  ts: new Date(Date.UTC(2025, 0, 1) + index * 86_400_000 * 4).toISOString(),
  equity: (10_000 + Math.sin(index / 11) * 140 + index * 1.26).toFixed(2),
}));

/** The control, on the same stamps — falling, which is what makes G8 and G10 argue. */
const RESEARCH_CONTROL = RESEARCH_CURVE.map((point, index) => ({
  ts: point.ts,
  equity: (10_000 + Math.sin(index / 9) * 220 - index * 3.6).toFixed(2),
}));

const RESEARCH_SWEEP = {
  run_id: 3,
  state: 'done',
  configurations_tried: 9,
  configurations_executed: 9,
  configurations_failed: 0,
  complete: true,
  grid: {
    metric: 'total_return_pct',
    dimensions: [
      { name: 'fast_period', values: ['16', '20', '25'] },
      { name: 'stop_pct', values: ['6.0', '8.0', '10.0'] },
    ],
    size: 9,
  },
  baseline: {
    parameters: { fast_period: '20', slow_period: '60', stop_pct: '8.0', risk_pct: '1.0' },
    in_grid: true,
    score: '1.8246',
    rank: 3,
    ranked_against: 9,
  },
  best: {
    index: 3,
    parameters: { fast_period: '20', stop_pct: '6.0' },
    trade_count: 6,
    score: '2.4328',
    error: null,
  },
  heatmap: {
    x: 'fast_period',
    y: 'stop_pct',
    x_values: ['16', '20', '25'],
    y_values: ['6.0', '8.0', '10.0'],
    rows: [
      ['2.3359', '2.4328', '1.7981'],
      ['1.7519', '1.8246', '1.3486'],
      ['1.4015', '1.4597', '1.0789'],
    ],
    held_at: {},
    covered: 9,
    cells: 9,
  },
  scatter: [],
  sensitivity: {
    centred_on_baseline: true,
    verdict: 'pass',
    shape: 'plateau',
    thresholds: { min_flank_pct: '10', plateau_retention_pct: '80', peak_retention_pct: '50' },
    positive_share_pct: '100.00',
    configurations_scored: 9,
    configurations_tried: 9,
    reasons: [],
    parameters: [
      {
        name: 'fast_period',
        centre_value: '20',
        verdict: 'pass',
        shape: 'plateau',
        retention_pct: '84.94',
        reason: 'fast_period: net expectancy stays positive at -20.00% and +25.00%.',
        lower: { value: '16', distance_pct: '-20.00', positive: true },
        upper: { value: '25', distance_pct: '25.00', positive: true },
      },
      {
        name: 'stop_pct',
        centre_value: '8.0',
        verdict: 'pass',
        shape: 'plateau',
        retention_pct: '89.31',
        reason: 'stop_pct: net expectancy stays positive at -25.00% and +25.00%.',
        lower: { value: '6.0', distance_pct: '-25.00', positive: true },
        upper: { value: '10.0', distance_pct: '25.00', positive: true },
      },
    ],
  },
  caveat:
    'A sweep result is only ever a shortlist. The headline of this run is the version\u2019s ' +
    'declared parameters — a configuration chosen before this data was searched — and the best ' +
    'of 9 is a candidate to re-run through a full backtest, not a result.',
  results: [
    ['16', '6.0', '2.3359', 7],
    ['20', '6.0', '2.4328', 6],
    ['25', '6.0', '1.7981', 7],
    ['16', '8.0', '1.7519', 7],
    ['20', '8.0', '1.8246', 6],
    ['25', '8.0', '1.3486', 7],
    ['16', '10.0', '1.4015', 7],
    ['20', '10.0', '1.4597', 6],
    ['25', '10.0', '1.0789', 7],
  ].map((row, index) => ({
    config_index: index,
    parameters: { fast_period: row[0], stop_pct: row[1] },
    metrics: { expectancy: '1.48' },
    trade_count: row[3] as number,
    score: row[2] as string,
    error: null,
  })),
  request_id: 'preview',
};

const RESEARCH_VALIDATIONS = { validations: [], total: 0, request_id: 'preview' };

const ROUTES: Array<[RegExp, unknown]> = [
  [/^\/api\/v1\/instruments\/\d+$/, INSTRUMENT],
  [/^\/api\/v1\/data-health$/, HEALTH],
  [/^\/api\/v1\/health\/deep$/, DEEP_HEALTH],
  [/^\/api\/v1\/health$/, { service: 'jmt-api', version: '0.1.0', status: HealthStatus.DEGRADED }],
  [/^\/api\/v1\/bars$/, BARS],
  // ⚠ Before the bare `/fleet` entry is not required — that one is `$`-anchored — but the more
  // specific pattern is listed first anyway, because the day someone drops an anchor is the day
  // ordering starts mattering and a fixture that only works by luck is worse than one that does not.
  [/^\/api\/v1\/fleet\/\d+\/fills$/, FLEET_FILLS],
  [/^\/api\/v1\/fleet\/\d+\/positions$/, FLEET_POSITIONS],
  // ⚠ Matched on pathname only, so the `?q=` a search sends is ignored and the fixture answers
  // every term the same way. The stub proves the view renders; the filtering is the query's job
  // and is covered in `test_api_fleet.py`.
  [/^\/api\/v1\/fleet\/fills$/, FLEET_HISTORY],
  [/^\/api\/v1\/fleet$/, FLEET],
  // S5 Research. Order matters here — `/backtests/\d+/trades` must precede `/backtests`.
  [/^\/api\/v1\/strategies$/, RESEARCH_STRATEGIES],
  [/^\/api\/v1\/backtests\/\d+\/trades$/, RESEARCH_TRADES],
  [/^\/api\/v1\/backtests\/\d+\/equity-curve$/, RESEARCH_CURVE],
  [/^\/api\/v1\/backtests\/\d+\/benchmark-curve$/, RESEARCH_CONTROL],
  [/^\/api\/v1\/backtests$/, RESEARCH_RUNS],
  [/^\/api\/v1\/sweeps\/\d+$/, RESEARCH_SWEEP],
  [/^\/api\/v1\/validations$/, RESEARCH_VALIDATIONS],
  // ⚠ `null` is served as a **404**, not as a 200 with an empty body. `GatePanel` treats a 404 as
  // "no holdout declared" and anything else as an error, and most versions have no holdout — so a
  // fixture that answered 200 would preview a state the product almost never shows.
  [/^\/api\/v1\/holdouts\/\d+$/, null],
];

/**
 * The fixture stub plus one screen, with no preview chrome of its own.
 *
 * Extracted so `ShellPreview` can render the same screens inside the real sidebar, top bar and
 * status rail. ⚠ **The stub lives here rather than in either caller**: two copies of a `fetch`
 * monkey-patch is two ways for a preview to disagree with the fixture, and the tear-down is the
 * half that would rot first.
 */
export function PreviewScreens({ screen }: { screen: string }): React.JSX.Element {
  useLayoutEffect(() => {
    const real = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = new URL(
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
        window.location.origin,
      );
      const hit = ROUTES.find(([re]) => re.test(url.pathname));
      if (!hit) return real(input, init);
      // A `null` fixture is a 404 rather than a 200 carrying nothing — see the holdout entry.
      return new Response(hit[1] === null ? '{"code":"not_found"}' : JSON.stringify(hit[1]), {
        status: hit[1] === null ? 404 : 200,
        headers: { 'content-type': 'application/json' },
      });
    };
    return () => {
      window.fetch = real;
    };
  }, []);

  const client = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { retry: false } } }),
    [],
  );

  const stocks = STOCKS[screen];
  const built =
    screen === 'markets' ||
    screen === 'system' ||
    screen === 'fleet' ||
    screen === 'research' ||
    Boolean(stocks);

  return (
    <QueryClientProvider client={client}>
      {screen === 'markets' ? <MarketsScreen /> : null}
      {screen === 'system' ? <SystemScreen /> : null}
      {screen === 'fleet' ? <FleetScreen /> : null}
      {screen === 'research' ? <ResearchScreen /> : null}
      {stocks ? stocks() : null}
      {built ? null : (
        <div className="jmt-screen">
          <Placeholder screen={screen}>{PLACEHOLDER_COPY[screen] ?? ''}</Placeholder>
        </div>
      )}
    </QueryClientProvider>
  );
}

export function ScreenPreview(): React.JSX.Element {
  // ⚠ Seeded from `?screen=`, so a specific preview is linkable and — the reason it exists —
  // reachable by `ops/screenshot_ui.py`, which drives a URL and cannot click a segmented
  // control. Without it the populated fleet table could only be photographed by hand, which is
  // exactly how it went five sessions without anyone noticing its hero was three type steps
  // too small.
  const [screen, setScreen] = useState(
    () => new URLSearchParams(window.location.search).get('screen') ?? 'markets',
  );

  /* Installed in a layout effect so it is in place before the screens' first render, and
     torn down on unmount so navigating away from the preview leaves the real `fetch` alone.
     Anything not in ROUTES falls through to the original, which will fail — correctly, since
     an unstubbed endpoint is a gap in the fixture rather than something to paper over. */
  useLayoutEffect(() => {
    const real = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = new URL(
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
        window.location.origin,
      );
      const hit = ROUTES.find(([re]) => re.test(url.pathname));
      if (!hit) return real(input, init);
      // A `null` fixture is a 404 rather than a 200 carrying nothing — see the holdout entry.
      return new Response(hit[1] === null ? '{"code":"not_found"}' : JSON.stringify(hit[1]), {
        status: hit[1] === null ? 404 : 200,
        headers: { 'content-type': 'application/json' },
      });
    };
    return () => { window.fetch = real; };
  }, []);

  const client = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { retry: false } } }),
    [],
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-canvas)' }}>
      <div className="jmt-screen jmt-screen--capped" style={{ paddingBottom: 0 }}>
        <Banner
          tone="paper"
          title="Fixture data, no backend"
          detail="The screens below are the real modules over a stubbed fetch. Layout, type, colour and every data-shape-dependent state are real; the API contract and error handling are not exercised. The fixtures are deliberately awkward — one stale series, one ageing, three synthetic bars, one open gap, a degraded health check, and a fleet holding every significance state including one flagged candidate and one with no measurements yet."
        />
        {/* ⚠ **The picker scrolls rather than overflowing the page.** `SegmentedControl` is
            vendored and sets its width from its content inline, so seven options measure 587px —
            which made the *document* 589px wide at every phone size and left every mobile
            screenshot of every screen looking broken by the preview rather than by the screen. It
            went from three options to seven when the Stocks screens were added; the container is
            the fix, because the control cannot be told to shrink from CSS. */}
        <div className="jmt-preview-picker">
          <SegmentedControl label="Screen" value={screen} onChange={setScreen} options={SCREENS} />
        </div>
      </div>
      <QueryClientProvider client={client}>
        {screen === 'markets' ? <MarketsScreen /> : null}
        {screen === 'system' ? <SystemScreen /> : null}
        {screen === 'fleet' ? <FleetScreen /> : null}
      {screen === 'research' ? <ResearchScreen /> : null}
        {STOCKS[screen] ? STOCKS[screen]!() : null}
      </QueryClientProvider>
    </div>
  );
}
